import React, { useCallback, useRef } from 'react'
import cls from './form.module.scss'
import { FormElementsGroup } from '@/shared/ui/forms/FormElementsGroup/FormElementsGroup'
import RoundedButton from '@/shared/ui/buttons/RoundedButton/RoundedButton'
import { RichText } from '@/shared/ui/RichText/RichText'
import classNames from 'classnames'

export const Form = props => {
  const {
    name,
    buttonLabel,
    buttonInfo,
    buttonId,
    buttonName,
    isSubmitting,
    elements = [],
    values,
    externalErrors,
    onChangeField,
    onSubmitClick,
    moreComps,
    classes,
    noForm,
  } = props

  const fieldsRef = useRef(null)

  const lastInputTimestampRef = useRef(0)
  const rapidInputBurstRef = useRef(0)
  const autofillSubmitGuardUntilRef = useRef(0)
  const lastTrustedPointerDownRef = useRef(0)

  const handleInputCapture = useCallback(event => {
    if (!event.isTrusted) return
    const now = Date.now()
    if (now - lastInputTimestampRef.current < 80) {
      rapidInputBurstRef.current += 1
      if (rapidInputBurstRef.current >= 2) {
        autofillSubmitGuardUntilRef.current = now + 500
      }
    } else {
      rapidInputBurstRef.current = 1
    }
    lastInputTimestampRef.current = now
  }, [])

  const handleTrustedPointerDown = useCallback(event => {
    if (event.isTrusted) {
      lastTrustedPointerDownRef.current = Date.now()
    }
  }, [])

  const performSubmit = useCallback(() => {
    if (Date.now() < autofillSubmitGuardUntilRef.current) {
      return
    }
    if (!fieldsRef.current || !fieldsRef.current.checkValidity()) {
      return
    }

    onSubmitClick && onSubmitClick(fieldsRef.current.getValues())
  }, [onSubmitClick])

  const handleSubmit = useCallback(
    event => {
      event?.preventDefault()
      performSubmit()
    },
    [performSubmit],
  )

  const handleButtonClick = useCallback(
    event => {
      if (event && event.isTrusted === false) return
      if (Date.now() - lastTrustedPointerDownRef.current > 500) return
      lastTrustedPointerDownRef.current = 0
      event?.preventDefault()
      performSubmit()
    },
    [performSubmit],
  )

  const handleKeyDown = useCallback(
    event => {
      if (event.key !== 'Enter') return
      if (event.isTrusted === false) return
      const tag = event.target?.tagName
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return
      event.preventDefault()
      performSubmit()
    },
    [performSubmit],
  )

  const content = (
    <>
      <div className={cls.inputsContainer}>
        <FormElementsGroup
          elements={elements}
          values={values}
          externalErrors={externalErrors}
          onChangeField={onChangeField}
          ref={fieldsRef}
        />
      </div>
      {buttonLabel && (
        <div className={cls.buttonContainer}>
          {buttonInfo && (
            <RichText className={classNames(cls.buttonInfo, classes?.buttonInfo)}>
              {buttonInfo}
            </RichText>
          )}
          <RoundedButton
            id={buttonId}
            name={buttonName}
            type={noForm ? 'button' : 'submit'}
            onClick={noForm ? handleButtonClick : undefined}
            onPointerDown={noForm ? handleTrustedPointerDown : undefined}
            loading={isSubmitting}
            disabled={isSubmitting}
            variant="DARK"
          >
            {buttonLabel}
          </RoundedButton>
        </div>
      )}
      {moreComps && moreComps}
    </>
  )

  if (noForm) {
    return (
      <div className={cls.root} onKeyDown={handleKeyDown} onInputCapture={handleInputCapture}>
        {content}
      </div>
    )
  }

  return (
    <form noValidate className={cls.root} name={name} onSubmit={handleSubmit}>
      {content}
    </form>
  )
}
